import {
	DemoMode,
	MousedOverTextHelper,
	OpenedInTabStories,
	PendingTourStepAtom,
	ShowFileUploader,
	ShowLandscapePreview,
	ShowPortraitPreview,
	ShowTour,
	ShowTourSelectionAtom,
} from "../../state/uiAtoms";
import { text } from "@quickpoint.me/shared";
import {
	useRecoilState,
	useRecoilValue,
	useSetRecoilState,
} from "recoil";

import { EditorIsClean } from "../../state/atoms";
import { ShowStacks } from "../../state/stackAtoms";
import { IsInPortrait } from "../../state/selectors";
import { TopBarControl } from "../StyledControl";
import { useEffect } from "react";
import { useClosePanelsForTour } from "../../hooks/useClosePanelsForTour";
import { useSaveStack } from "../../hooks/useSaveStack";
import { useStartTour } from "../../hooks/useStartTour";
import { useTour } from "@reactour/tour";
import { useMetrics } from "../../hooks/useMetrics";

function TourControl() {
	const setTextHelper = useSetRecoilState(MousedOverTextHelper);
	const [showTour, setShowTour] = useRecoilState(ShowTour);
	const setPortrait = useSetRecoilState(ShowPortraitPreview);
	const setLandscape = useSetRecoilState(ShowLandscapePreview);
	const [showFileUploader, setShowFileUploader] =
		useRecoilState(ShowFileUploader);
	const editorIsClean = useRecoilValue(EditorIsClean);
	const demoMode = useRecoilValue(DemoMode);
	const isInPortrait = useRecoilValue(IsInPortrait);
	const setOpenedInTabStories = useSetRecoilState(OpenedInTabStories);
	const setShowTourSelection = useSetRecoilState(ShowTourSelectionAtom);
	const [pendingTourStep, setPendingTourStep] =
		useRecoilState(PendingTourStepAtom);
	const save = useSaveStack();
	const { trackFeature } = useMetrics();

	const { setIsOpen, setCurrentStep } = useTour();

	const setShowStacks = useSetRecoilState(ShowStacks);
	const closePanelsForTour = useClosePanelsForTour();
	const startTour = useStartTour();
	const handleClick = async () => {
		if (!editorIsClean) {
			save();
		}
		// close the presentations popup first — it would cover the tour
		// selection modal and the tour itself
		setShowStacks(false);
		if (isInPortrait) {
			// portrait has exactly one tour (mobile) — skip the chooser
			startTour("mobile");
		} else {
			// Open the tour selection modal instead of starting directly
			setShowTourSelection(true);
		}
		trackFeature('tour_button');
	};

	useEffect(() => {
		setShowTour(demoMode);
	}, [demoMode, setShowTour]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: keep showFileUploader isolated
	useEffect(() => {
		if (showTour) {
			// the tour must never play underneath a modal — the first-signup
			// flow arrives here with the presentations panel freshly opened
			closePanelsForTour();
			setIsOpen(showTour);
			setCurrentStep(pendingTourStep ?? 0);
			if (pendingTourStep !== null) setPendingTourStep(null);
			setPortrait(false);
			setLandscape(true);
			setOpenedInTabStories([]);
		} else {
			setShowTour(false);
			setIsOpen(false);

			if (showFileUploader) setShowFileUploader(false);
		}
	}, [
		showTour,
		setIsOpen,
		setShowTour,
		setOpenedInTabStories,
		setLandscape,
		setCurrentStep,
		setPortrait,
		setShowFileUploader,
	]);

	const mouseOver = () => {
		setTextHelper(text.controls.tour);
	};

	const mouseLeave = () => {
		setTextHelper(undefined);
	};

	return (
		<TopBarControl
			$isInPortrait={isInPortrait}
			className="top-bar-control tour"
			data-testid="tour-control"
			onClick={handleClick}
			onMouseOver={mouseOver}
			onMouseLeave={mouseLeave}
		>
			{text.controls.tour}
		</TopBarControl>
	);
}

export default TourControl;
